import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { ChatWidgetDemo, SlackDemo, StepsCard } from './HeroDemo';
import type { IndustryContent } from './types';
import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  Stage,
  tone,
  useCopyGap,
  useOpenAgentDemo,
} from '../primitives';

export default function IndustryHero({
  label,
  hero,
}: {
  /** The industry's name, as the breadcrumb shows it. */
  label: string;
  hero: IndustryContent['hero'];
}) {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');
  const gap = useCopyGap();
  const openDemo = useOpenAgentDemo();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto grid max-w-7xl gap-x-12 gap-y-14 px-6 pb-4 pt-10 sm:pt-14 lg:grid-cols-12 lg:items-start lg:px-8 lg:pt-14'>
        <div className='min-w-0 lg:col-span-6 lg:pt-10'>
          <nav aria-label={t('Breadcrumb')} className={styles.enter}>
            <ol
              className={clsxm(
                'inline-flex max-w-full items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                tone.quiet,
              )}
            >
              <li>
                <Link
                  href='/chatbots'
                  className='transition-colors hover:text-primary-700 dark:hover:text-primary-300'
                >
                  {t('AI Agents')}
                </Link>
              </li>
              <li aria-hidden='true' className='opacity-50'>
                /
              </li>
              <li aria-current='page' className={clsxm('truncate', tone.ink)}>
                {label}
              </li>
            </ol>
          </nav>

          <h1
            className={clsxm(
              styles.display,
              styles.heroTitle,
              styles.enter,
              'mt-7 text-[2.5rem] font-semibold leading-[1.05] sm:text-[3.25rem] sm:leading-[1.03] lg:text-[3.5rem] xl:text-[3.75rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            {hero.title}
            {gap}
            <span className='text-primary-600 dark:text-primary-400'>
              {hero.accent}
            </span>
          </h1>

          <p
            className={clsxm(
              styles.enter,
              'mt-6 max-w-xl text-lg leading-8 sm:text-xl sm:leading-[1.6]',
              tone.body,
            )}
            style={delay(140)}
          >
            {hero.lead}
          </p>

          <div
            className={clsxm(styles.enter, 'mt-8 flex flex-wrap gap-3')}
            style={delay(200)}
          >
            <Link href='/login?preset=bots' className={buttonPrimary}>
              {tc('Build your agent free')}
              <ArrowGlyph />
            </Link>
            <button
              type='button'
              onClick={openDemo}
              className={buttonSecondary}
            >
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
              </span>
              {tc('Talk to our agent')}
            </button>
          </div>
          <p
            className={clsxm(styles.enter, 'mt-5 text-sm', tone.quiet)}
            style={delay(240)}
          >
            {tc('No credit card required')} ·{' '}
            {tc('Ready in minutes, no code needed')}
          </p>
        </div>

        <div className='-mx-3 min-w-0 sm:mx-0 lg:col-span-6'>
          <Stage
            variant='indigo'
            halftoneAt='100% 0%'
            className={clsxm(
              styles.enter,
              'px-4 py-10 sm:px-10 sm:py-12 lg:px-8 xl:px-12',
            )}
            style={delay(280)}
          >
            <p className='sr-only'>{hero.description}</p>
            <div aria-hidden='true' className='relative mx-auto max-w-[24rem]'>
              {hero.surface === 'slack' && hero.person ? (
                <SlackDemo
                  brand={hero.brand}
                  messages={hero.messages}
                  person={hero.person}
                />
              ) : (
                <ChatWidgetDemo brand={hero.brand} messages={hero.messages} />
              )}
              <div className='relative -mt-8 ms-auto w-[88%] sm:w-[17.5rem] lg:-me-6 xl:-me-12'>
                <StepsCard steps={hero.steps} />
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </header>
  );
}
